import { useEffect, useState } from 'react';
import { ESSENTIAL_DISCOUNT, STANDARD_DISCOUNT } from '../constants/spread';
import { getListPrice } from 'src/common/hooks/widgets/fetchData';
import {
  essentialsAppPoints,
  standardAppPoints,
  switchBrand,
} from '../constants/default';
import { useCurrency } from 'src/common/hooks/widgets/useCurrency';

/**
 * Calculate the estimated price
 *
 * @param {object} intl used for the formatting
 * @param {string} localCurrency the language code to get the currency sign for
 * @param {number} employees : Number of employees/workstations
 * @param {boolean} breach : Includes Breach
 * @returns {Array}
 */

export interface PropsFunc {
  userAmount: number;
  envAmount: number;
  tileValueSelected: string;
  additionalUsers: boolean;
  nonProdEnv: boolean;
  onInspection: boolean;
  currency: string;
  localeCode: string;
  concurrentUsersAmt: number;
  authUsersAmt: number;
  healthScoresAmt: number;
  setTotalStdBaseAppPoints: React.Dispatch<React.SetStateAction<number>>;
  country: string;
  offering: string;
  spaceMgmtUser: boolean;
  spaceMgmtAddEnv: boolean;
  captPlanUser: boolean;
  captPlanAddEnv: boolean;
  leaseUser: boolean;
  leaseAddEnv: boolean;
  leaseNum: boolean;
  aiService: boolean;
  setPkg: React.Dispatch<React.SetStateAction<string>>;
}

export interface IFetchError {
  status: number;
  statusText: string;
}

export interface priceProps {
  partNumber: string;
  pricingIndicator: string;
  pricingModel: number;
  scaleQtyPrice: [
    {
      qty: number;
      price: number;
    },
  ];
}

// may need in future so commenting as of now
// type typeOfPart = {
//   qty: number;
//   price: number;
// };

const partNumber = {
  SaaS: 'D0A51ZX',
  IventSize: 'D0IK1ZX',
};

const partsToQuery = [
  {
    partNumber: partNumber.SaaS,
  },
  {
    partNumber: partNumber.IventSize,
  },
];

type MarketPlaceRequest = {
  currency: string;
  countryCode3: string;
  parts: typeof partsToQuery;
};

export const formatter = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

export const usePrice = ({
  currency,
  userAmount,
  envAmount,
  tileValueSelected,
  additionalUsers,
  nonProdEnv,
  onInspection,
  localeCode,
  concurrentUsersAmt,
  authUsersAmt,
  healthScoresAmt,
  setTotalStdBaseAppPoints,
  country,
  offering,
  spaceMgmtUser,
  spaceMgmtAddEnv,
  captPlanUser,
  captPlanAddEnv,
  leaseUser,
  leaseAddEnv,
  leaseNum,
  aiService,
  setPkg,
}: PropsFunc) => {
  const [totalCost, setTotalCost] = useCurrency(currency, localeCode, 0);
  const [totalCostMonthly, setTotalCostMonthly] = useCurrency(
    currency,
    localeCode,
    0,
  );
  const [appPointPrice, setAppPointPrice] = useState(0);
  const [inventSizeVal, setInventSizeVal] = useState(0);
  const [dataFetched, setDataFetched] = useState([]);
  const [fetchingError, setFetchingError] = useState({});
  const [loading, setLoading] = useState(false);
  const [totalEstAppPoints, setTotalEstAppPoints] = useState(0);
  const [priceVal, setPriceVal] = useState(0);

  const calculatedPrice = () => {
    let price;
    if (tileValueSelected === 'essentials') {
      if (offering === switchBrand.brand1) {
        if (!onInspection && !nonProdEnv && !additionalUsers && !aiService) {
          setTotalEstAppPoints(essentialsAppPoints.maintenanceBaseAppPoint);
          price =
            essentialsAppPoints.maintenanceBaseAppPoint *
            appPointPrice *
            ESSENTIAL_DISCOUNT;
          setPriceVal(price);
        } else if (
          !onInspection &&
          additionalUsers &&
          !nonProdEnv &&
          !aiService
        ) {
          setTotalEstAppPoints(
            essentialsAppPoints.maintenanceBaseAppPoint +
              essentialsAppPoints.additionalUserAppPoint,
          );
          price =
            (essentialsAppPoints.maintenanceBaseAppPoint +
              essentialsAppPoints.additionalUserAppPoint) *
            appPointPrice *
            ESSENTIAL_DISCOUNT;
          setPriceVal(price);
        } else if (
          !onInspection &&
          !additionalUsers &&
          nonProdEnv &&
          !aiService
        ) {
          setTotalEstAppPoints(
            essentialsAppPoints.maintenanceBaseAppPoint +
              essentialsAppPoints.additionalEnvAppPoint,
          );
          price =
            (essentialsAppPoints.maintenanceBaseAppPoint +
              essentialsAppPoints.additionalEnvAppPoint) *
            appPointPrice *
            ESSENTIAL_DISCOUNT;
          setPriceVal(price);
        } else if (
          !onInspection &&
          !additionalUsers &&
          !nonProdEnv &&
          aiService
        ) {
          setTotalEstAppPoints(
            essentialsAppPoints.maintenanceBaseAppPoint +
              essentialsAppPoints.aiService,
          );
          price =
            (essentialsAppPoints.maintenanceBaseAppPoint +
              essentialsAppPoints.aiService) *
            appPointPrice *
            ESSENTIAL_DISCOUNT;
          setPriceVal(price);
        } else if (
          !onInspection &&
          !additionalUsers &&
          nonProdEnv &&
          aiService
        ) {
          setTotalEstAppPoints(
            essentialsAppPoints.maintenanceBaseAppPoint +
              essentialsAppPoints.aiServiceEnv +
              essentialsAppPoints.additionalEnvAppPoint,
          );
          price =
            (essentialsAppPoints.maintenanceBaseAppPoint +
              essentialsAppPoints.aiService +
              essentialsAppPoints.additionalEnvAppPoint) *
            appPointPrice *
            ESSENTIAL_DISCOUNT;
          setPriceVal(price);
        } else if (
          !onInspection &&
          additionalUsers &&
          !nonProdEnv &&
          aiService
        ) {
          setTotalEstAppPoints(
            essentialsAppPoints.maintenanceBaseAppPoint +
              essentialsAppPoints.aiService +
              essentialsAppPoints.additionalUserAppPoint,
          );
          price =
            (essentialsAppPoints.maintenanceBaseAppPoint +
              essentialsAppPoints.aiService +
              essentialsAppPoints.additionalUserAppPoint) *
            appPointPrice *
            ESSENTIAL_DISCOUNT;
          setPriceVal(price);
        } else if (
          !onInspection &&
          additionalUsers &&
          nonProdEnv &&
          !aiService
        ) {
          setTotalEstAppPoints(
            essentialsAppPoints.maintenanceBaseAppPoint +
              essentialsAppPoints.additionalEnvAppPoint +
              essentialsAppPoints.additionalUserAppPoint,
          );
          price =
            (essentialsAppPoints.maintenanceBaseAppPoint +
              essentialsAppPoints.additionalEnvAppPoint +
              essentialsAppPoints.additionalUserAppPoint) *
            appPointPrice *
            ESSENTIAL_DISCOUNT;
          setPriceVal(price);
        } else if (
          !onInspection &&
          additionalUsers &&
          nonProdEnv &&
          aiService
        ) {
          setTotalEstAppPoints(
            essentialsAppPoints.maintenanceBaseAppPoint +
              essentialsAppPoints.additionalEnvAppPoint +
              essentialsAppPoints.additionalUserAppPoint +
              essentialsAppPoints.aiServiceEnv,
          );
          price =
            (essentialsAppPoints.maintenanceBaseAppPoint +
              essentialsAppPoints.additionalEnvAppPoint +
              essentialsAppPoints.additionalUserAppPoint +
              essentialsAppPoints.aiService) *
            appPointPrice *
            ESSENTIAL_DISCOUNT;
          setPriceVal(price);
        }
      } else if (offering === switchBrand.brand2) {
        setPkg('Essentials');
        price =
          essentialsAppPoints.inspectionBaseAppPoint *
          appPointPrice *
          ESSENTIAL_DISCOUNT;
        setTotalEstAppPoints(175);
        setPriceVal(price);
      } else if (offering === switchBrand.brand3) {
        if (!spaceMgmtUser && !spaceMgmtAddEnv) {
          setTotalEstAppPoints(150);
          setPkg('Essentials');
          price = 150 * appPointPrice * ESSENTIAL_DISCOUNT;
          setPriceVal(price);
        } else if (spaceMgmtUser && !spaceMgmtAddEnv) {
          setTotalEstAppPoints(200);
          setPkg('Essentials');
          price = 200 * appPointPrice * ESSENTIAL_DISCOUNT;
          setPriceVal(price);
        } else if (!spaceMgmtUser && spaceMgmtAddEnv) {
          setTotalEstAppPoints(198);
          setPkg('Essentials');
          price = 198 * appPointPrice * ESSENTIAL_DISCOUNT;
          setPriceVal(price);
        } else if (spaceMgmtUser && spaceMgmtAddEnv) {
          setTotalEstAppPoints(248);
          setPkg('Essentials');
          price = 248 * appPointPrice * ESSENTIAL_DISCOUNT;
          setPriceVal(price);
        }
      } else if (offering === switchBrand.brand4) {
        if (!captPlanUser && !captPlanAddEnv) {
          setTotalEstAppPoints(150);
          setPkg('Essentials');
          price = 150 * appPointPrice * ESSENTIAL_DISCOUNT;
          setPriceVal(price);
        } else if (captPlanUser && !captPlanAddEnv) {
          setTotalEstAppPoints(200);
          setPkg('Essentials');
          price = 200 * appPointPrice * ESSENTIAL_DISCOUNT;
          setPriceVal(price);
        } else if (!captPlanUser && captPlanAddEnv) {
          setTotalEstAppPoints(198);
          setPkg('Essentials');
          price = 198 * appPointPrice * ESSENTIAL_DISCOUNT;
          setPriceVal(price);
        } else if (captPlanUser && captPlanAddEnv) {
          setTotalEstAppPoints(248);
          setPkg('Essentials');
          price = 248 * appPointPrice * ESSENTIAL_DISCOUNT;
          setPriceVal(price);
        }
      } else if (offering === switchBrand.brand5) {
        if (!leaseUser && !leaseAddEnv && !leaseNum) {
          setTotalEstAppPoints(150);
          setPkg('Essentials');
          price = 150 * appPointPrice * ESSENTIAL_DISCOUNT;
          setPriceVal(price);
        } else if (leaseUser && !leaseAddEnv && !leaseNum) {
          setTotalEstAppPoints(210);
          setPkg('Essentials');
          price = 210 * appPointPrice * ESSENTIAL_DISCOUNT;
          setPriceVal(price);
        } else if (!leaseUser && leaseAddEnv && !leaseNum) {
          setTotalEstAppPoints(198);
          setPkg('Essentials');
          price = 198 * appPointPrice * ESSENTIAL_DISCOUNT;
          setPriceVal(price);
        } else if (!leaseUser && !leaseAddEnv && leaseNum) {
          setTotalEstAppPoints(160);
          setPkg('Essentials');
          price = 160 * appPointPrice * ESSENTIAL_DISCOUNT;
          setPriceVal(price);
        } else if (leaseUser && leaseAddEnv && !leaseNum) {
          setTotalEstAppPoints(258);
          setPkg('Essentials');
          price = 258 * appPointPrice * ESSENTIAL_DISCOUNT;
          setPriceVal(price);
        } else if (leaseUser && !leaseAddEnv && leaseNum) {
          setTotalEstAppPoints(220);
          setPkg('Essentials');
          price = 220 * appPointPrice * ESSENTIAL_DISCOUNT;
          setPriceVal(price);
        } else if (!leaseUser && leaseAddEnv && leaseNum) {
          setTotalEstAppPoints(208);
          setPkg('Essentials');
          price = 208 * appPointPrice * ESSENTIAL_DISCOUNT;
          setPriceVal(price);
        } else if (leaseUser && leaseAddEnv && leaseNum) {
          setTotalEstAppPoints(268);
          setPkg('Essentials');
          price = 268 * appPointPrice * ESSENTIAL_DISCOUNT;
          setPriceVal(price);
        }
      } else if (offering === switchBrand.brand6) {
        setTotalEstAppPoints(140);
        price = 140 * appPointPrice * ESSENTIAL_DISCOUNT;
        setPkg('Essentials');
        setPriceVal(price);
      }
    } else if (tileValueSelected === 'standard') {
      const totalAppPoints = getStdAppPoints();
      if (totalAppPoints <= standardAppPoints.minPoint) {
        price = standardAppPoints.minPoint * appPointPrice * STANDARD_DISCOUNT;
        setPriceVal(price);
      } else {
        price = totalAppPoints * appPointPrice * STANDARD_DISCOUNT;
        setPriceVal(price);
      }
    }
    return price;
  };

  const getStdAppPoints = () => {
    const totalPoints =
      standardAppPoints.default +
      standardAppPoints.concurrentUsers * concurrentUsersAmt +
      standardAppPoints.authorizedUsers * authUsersAmt +
      healthScoresAmt / standardAppPoints.healthScores;
    return totalPoints;
  };

  const setPricesData = (products: priceProps[]) => {
    if (products?.length) {
      const priceArray = products.map((item) => ({
        [item.partNumber]: item.scaleQtyPrice,
      }));
      const appPointPrice = priceArray[0].D0A51ZX;
      const inventSizeVal = priceArray[1].D0IK1ZX;
      setAppPointPrice(appPointPrice[0].price);
      setInventSizeVal(Math.ceil(inventSizeVal[0].price * 10) / 10);
    }
  };

  const fetchPrices = () => {
    setLoading(true);
    const body: MarketPlaceRequest = {
      currency: currency,
      countryCode3: country,
      parts: partsToQuery,
    };
    getListPrice(
      `https://api.marketplace.ibm.com/purchase/catalog/resources/price/info`,
      JSON.stringify(body),
      'POST',
    )
      .then((data) => {
        if (data.parts && data.parts.length !== 0) {
          setLoading(false);
          setPricesData(data.parts);
          setDataFetched(data.parts);
        } else {
          setLoading(false);
          setDataFetched([]);
        }
      })
      .catch((error) => {
        setLoading(false);
        setDataFetched([]);
        if (error.response) {
          setFetchingError(error.response);
        }
      });
  };

  // const price = calculatedPrice()!;
  const stdAppPoints = getStdAppPoints();

  useEffect(() => {
    if (currency && dataFetched.length === 0) {
      fetchPrices();
    }
  }, [currency]);

  useEffect(() => {
    calculatedPrice();
  }, [
    envAmount,
    userAmount,
    tileValueSelected,
    appPointPrice,
    onInspection,
    offering,
    spaceMgmtUser,
    spaceMgmtAddEnv,
    captPlanAddEnv,
    captPlanUser,
    leaseUser,
    leaseAddEnv,
    offering,
    aiService,
    leaseNum,
    concurrentUsersAmt,
    authUsersAmt,
    healthScoresAmt,
  ]);

  useEffect(() => {
    setTotalCost(priceVal * 12);
    setTotalCostMonthly(priceVal);
    setTotalStdBaseAppPoints(stdAppPoints);
  }, [
    envAmount,
    userAmount,
    tileValueSelected,
    appPointPrice,
    priceVal,
    onInspection,
    offering,
    spaceMgmtUser,
    spaceMgmtAddEnv,
    captPlanAddEnv,
    captPlanUser,
    leaseUser,
    leaseAddEnv,
    offering,
    aiService,
    leaseNum,
    concurrentUsersAmt,
    authUsersAmt,
    healthScoresAmt,
  ]);

  return [
    totalCost,
    totalCostMonthly,
    loading,
    totalEstAppPoints,
    fetchingError,
    inventSizeVal,
  ];
};
